import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, edgeFnUrl } from '@saas-boilerplate/shared';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Mail, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import appLogo from '@/assets/upstand_ai_logo.svg';
import { AttendeeMeetingView } from './attendee-meeting-view';

interface SlugMeta {
  found: boolean;
  series_id?: string;
  workspace_id?: string;
  title?: string;
  next_meeting?: { id: string; starts_at: string; ends_at: string } | null;
}

type GatePhase =
  | 'resolving'
  | 'not_found'
  | 'email_prompt'
  | 'code_prompt'
  | 'verifying'
  | 'not_attendee'
  | 'ready';

export function AttendeeGate({
  slug,
  onMemberHandoff,
}: {
  slug: string;
  /** When the visitor is a full workspace member, hand off to the main app client-side (no full
   *  page reload / second boot) instead of window.location. Falls back to a hard redirect. */
  onMemberHandoff?: (meetingId: string | null) => void;
}) {
  const [phase, setPhase] = useState<GatePhase>('resolving');
  const [meta, setMeta] = useState<SlugMeta | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isFullMember, setIsFullMember] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Keep the code field focused so the user can switch to this tab and paste without clicking. autoFocus
  // only fires on mount, so also re-focus when we enter the code screen and whenever the tab/window
  // regains focus (the common flow: go to the email tab for the code, switch back, paste).
  useEffect(() => {
    if (phase !== 'code_prompt') return;
    const focus = () => {
      if (!document.hidden) codeInputRef.current?.focus();
    };
    focus();
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    return () => {
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', focus);
    };
  }, [phase]);

  // Full workspace members don't need the attendee shell - send them to the main app. Done in an
  // effect (not during render) so we can hand off via React state without a "setState during
  // render" violation. Prefer the client-side hand-off (no reload); fall back to a hard redirect,
  // and keep a safety net in case the hand-off doesn't take us off this route.
  useEffect(() => {
    if (phase !== 'ready' || !isFullMember || !meta?.series_id) return;
    const meetingId = meta?.next_meeting?.id ?? null;
    const hardRedirect = () => {
      if (typeof window !== 'undefined') {
        window.location.replace(
          meetingId ? `/meetings?meeting=${encodeURIComponent(meetingId)}` : '/meetings',
        );
      }
    };
    if (!onMemberHandoff) {
      hardRedirect();
      return;
    }
    onMemberHandoff(meetingId);
    const t = setTimeout(() => {
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/meet/direct/')) {
        hardRedirect();
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [phase, isFullMember, meta, onMemberHandoff]);

  const resolveSlug = useCallback(async () => {
    const { data, error } = await supabase.rpc('resolve_meeting_slug', { p_slug: slug });
    if (error || !data?.found) {
      setPhase('not_found');
      return null;
    }
    setMeta(data as SlugMeta);
    return data as SlugMeta;
  }, [slug]);

  const checkSession = useCallback(
    async (resolved: SlugMeta) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        setPhase('email_prompt');
        return;
      }

      setUserId(session.user.id);
      const userEmail = session.user.email?.toLowerCase().trim();

      // Check if user is a full workspace member - if so, redirect
      if (resolved.workspace_id) {
        const { data: wm } = await supabase
          .from('workspace_members')
          .select('id')
          .eq('workspace_id', resolved.workspace_id)
          .eq('user_id', session.user.id)
          .eq('status', 'active')
          .maybeSingle();
        if (wm) {
          setIsFullMember(true);
          setPhase('ready');
          return;
        }
      }

      // Verify email is in attendee list for any meeting in this series
      if (!userEmail || !resolved.series_id) {
        setPhase('not_attendee');
        return;
      }

      // RLS-gated fetch: if user can access any meeting in the series, they're a valid attendee
      const { data: accessibleMeetings } = await supabase
        .from('meetings')
        .select('id')
        .eq('series_id', resolved.series_id)
        .limit(1);

      if (accessibleMeetings && accessibleMeetings.length > 0) {
        setPhase('ready');
        return;
      }

      setPhase('not_attendee');
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await resolveSlug();
      if (cancelled || !resolved) return;
      await checkSession(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveSlug, checkSession]);

  // Listen for auth state changes (magic link callback)
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
        session?.user &&
        meta
      ) {
        setUserId(session.user.id);
        setPhase('verifying');
        await checkSession(meta);
      }
    });
    return () => subscription.unsubscribe();
  }, [meta, checkSession]);

  const requestCode = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter your email address');
      return;
    }
    setSending(true);
    try {
      const { data: eligible, error: eligibilityError } = await supabase.rpc(
        'attendee_email_eligible_for_slug',
        { p_slug: slug, p_email: trimmed },
      );
      if (eligibilityError) throw eligibilityError;
      if (!eligible) {
        toast.error('This email is not on the invite list for this meeting.', {
          description:
            'Use the same address as on the calendar invite. If you were added recently, ask the organiser to sync attendees.',
        });
        return;
      }

      const redirectBase =
        typeof window !== 'undefined'
          ? window.location.origin
          : import.meta.env.VITE_APP_URL?.replace(/\/$/, '') || '';
      const redirectTo = `${redirectBase}/meet/direct/${encodeURIComponent(slug)}`;

      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!anonKey) {
        toast.error('App configuration error', {
          description: 'Missing Supabase anon key.',
        });
        return;
      }

      const fnUrl = edgeFnUrl('auth-attendee-magic-link');
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
        },
        body: JSON.stringify({ email: trimmed, slug, redirectTo }),
      });

      let result: { success?: boolean; error?: string; details?: string; message?: string } = {};
      try {
        result = await res.json();
      } catch {
        /* ignore */
      }

      if (!res.ok) {
        const msg = result.error || result.details || `Request failed (${res.status})`;
        const isRateLimit = res.status === 429 || /rate limit|429/i.test(String(msg));
        toast.error(isRateLimit ? 'Email rate limit exceeded' : 'Failed to send code', {
          description: isRateLimit
            ? 'Try again in a few minutes. If you were testing repeatedly, Supabase or your email provider may be throttling sends.'
            : msg,
          duration: isRateLimit ? 8000 : 5000,
        });
        return;
      }

      setCode('');
      setPhase('code_prompt');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send code');
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    const trimmedCode = code.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedCode) {
      toast.error('Please enter the code from your email');
      return;
    }
    setVerifying(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedCode,
        type: 'magiclink',
      });
      if (error) {
        // Invalid or expired code - stay on code_prompt so user can retry or resend
        toast.error('Invalid or expired code', {
          description: 'Please check the code from your email or request a new one.',
        });
      }
      // On success, onAuthStateChange fires SIGNED_IN → checkSession handles the rest
    } catch (e: any) {
      toast.error(e?.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // --- Renders ---

  if (phase === 'resolving' || phase === 'verifying') {
    return (
      <AttendeeShell>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-amber-700/70" />
          <p className="font-display text-sm text-stone-500">
            {phase === 'resolving' ? 'Loading meeting…' : 'Verifying access…'}
          </p>
        </div>
      </AttendeeShell>
    );
  }

  if (phase === 'not_found') {
    return (
      <AttendeeShell>
        <AttendeeCard>
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-light tracking-tight text-foreground">
                Meeting not found
              </h2>
              <p className="mt-2 text-sm text-stone-500 max-w-xs">
                This link doesn't match any meeting. Please check the link from your calendar invite.
              </p>
            </div>
          </div>
        </AttendeeCard>
      </AttendeeShell>
    );
  }

  if (phase === 'not_attendee') {
    return (
      <AttendeeShell>
        <AttendeeCard>
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
              <AlertTriangle className="h-7 w-7 text-amber-600" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-light tracking-tight text-foreground">
                Not on the invite list
              </h2>
              <p className="mt-2 text-sm text-stone-500 max-w-xs">
                Your email doesn't match any attendee on this meeting. If you've been added recently,
                the organiser may need to sync the attendee list.
              </p>
            </div>
            <button
              type="button"
              className="rounded-md border border-stone-200 bg-white/80 px-5 py-2.5 text-sm font-medium text-stone-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
              onClick={async () => {
                await supabase.auth.signOut();
                setPhase('email_prompt');
              }}
            >
              Try a different email
            </button>
          </div>
        </AttendeeCard>
      </AttendeeShell>
    );
  }

  if (phase === 'email_prompt') {
    return (
      <AttendeeShell>
        <AttendeeCard wide>
          <div className="flex flex-col gap-6">
            {meta?.title && (
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-widest text-stone-400">
                  You're invited to
                </p>
                <h2 className="mt-1 font-display text-2xl font-light tracking-tight text-foreground">
                  {meta.title}
                </h2>
                {meta.next_meeting?.starts_at && (
                  <p className="mt-1.5 text-sm text-stone-500">
                    {new Date(meta.next_meeting.starts_at).toLocaleDateString(undefined, {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="attendee-email"
                  className="text-xs font-semibold uppercase tracking-widest text-stone-400"
                >
                  Your email address
                </Label>
                <Input
                  id="attendee-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && requestCode()}
                  autoFocus
                  className="border-stone-200 bg-white/70 backdrop-blur-sm focus-visible:ring-amber-600/30"
                />
                <p className="text-xs text-stone-400">
                  Use the address that received the calendar invite.
                </p>
              </div>
              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-md py-3 font-display text-sm font-medium uppercase tracking-widest text-white onboarding-primary-gradient disabled:opacity-60"
                onClick={requestCode}
                disabled={sending}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                {sending ? 'Sending…' : 'Send access code'}
              </button>
            </div>
          </div>
        </AttendeeCard>
      </AttendeeShell>
    );
  }

  if (phase === 'code_prompt') {
    return (
      <AttendeeShell>
        <AttendeeCard wide>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
                <Mail className="h-7 w-7 text-amber-700" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-light tracking-tight text-foreground">
                  Check your inbox
                </h2>
                <p className="mt-1.5 text-sm text-stone-500">
                  We sent a code to{' '}
                  <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="attendee-code"
                  className="text-xs font-semibold uppercase tracking-widest text-stone-400"
                >
                  Access code
                </Label>
                <Input
                  ref={codeInputRef}
                  id="attendee-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="--------"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && verifyCode()}
                  autoFocus
                  autoComplete="one-time-code"
                  className="border-stone-200 bg-white/70 text-center text-xl tracking-[0.4em] backdrop-blur-sm focus-visible:ring-amber-600/30"
                />
              </div>
              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-md py-3 font-display text-sm font-medium uppercase tracking-widest text-white onboarding-primary-gradient disabled:opacity-60"
                onClick={verifyCode}
                disabled={verifying}
              >
                {verifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {verifying ? 'Verifying…' : 'Verify code'}
              </button>
            </div>

            <div className="flex flex-col items-center gap-1.5 text-xs text-stone-400">
              <button
                type="button"
                onClick={() => { setSending(false); requestCode(); }}
                className="hover:text-stone-700 underline-offset-4 hover:underline transition-colors"
                disabled={sending}
              >
                {sending ? 'Sending…' : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={() => setPhase('email_prompt')}
                className="hover:text-stone-700 underline-offset-4 hover:underline transition-colors"
              >
                Use a different email
              </button>
            </div>
          </div>
        </AttendeeCard>
      </AttendeeShell>
    );
  }

  // phase === 'ready'
  if (!meta?.series_id) {
    return (
      <AttendeeShell>
        <Loader2 className="h-8 w-8 animate-spin text-amber-700/70" />
      </AttendeeShell>
    );
  }

  // Signed-in workspace members get sent to the main app view - handled by the effect above (a
  // client-side hand-off, or a hard-redirect fallback). Just show a loader while that happens.
  if (isFullMember) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <AttendeeMeetingView
        seriesId={meta.series_id}
        initialMeetingId={meta.next_meeting?.id ?? null}
        userId={userId!}
        seriesTitle={meta.title ?? 'Meeting'}
      />
      <Toaster />
    </>
  );
}

// --- Sub-components ---

function AttendeeHeader() {
  return (
    <header className="sticky top-0 z-50 flex w-full items-center border-b border-border/40 bg-background/80 px-4 py-3 backdrop-blur-xl sm:px-6">
      <img
        src={appLogo}
        alt="Upstand.ai"
        className="h-8 w-auto max-w-[200px] object-contain object-left sm:h-9"
      />
    </header>
  );
}

function AttendeeShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground">
      <AttendeeHeader />
      <main className="flex flex-1 items-center justify-center p-6">
        {children}
      </main>
      <Toaster />
    </div>
  );
}

function AttendeeCard({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div
      className={`w-full rounded-xl border border-outline-variant/15 bg-white/60 p-8 shadow-sm backdrop-blur-xl ${wide ? 'max-w-sm' : 'max-w-xs'}`}
    >
      {children}
    </div>
  );
}
